import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@fc/utilities';
import PaginatedList from '@fc/pagination';
import { FCEvents } from '@fc/events';

const FIT_RESULTS_TAG = 'search-results';
const FIT_GRID_ATTRIBUTE = 'product-grid-view';
const FIT_LAYOUT_FALLBACK = 'default';
const FIT_TRANSITION_SCOPE = ['product-grid'];
const FIT_VIEW_STORAGE_PREFIX = 'product-grid-view-';
const FIT_VIEWPORT_SELECTORS = {
  desktop: '[data-grid-layout="desktop-default-option"]',
  mobile: '[data-grid-layout="mobile-option"]',
};
const FIT_IMAGE_TOGGLE_KEY = 'fit-image-view-alt';
const FIT_TOGGLE_INPUT_SELECTOR = 'input[name="fit_image_toggle"]';

// Paginated results with grid/list view switching and viewport persistence
export default class ResultsList extends PaginatedList {
  #abortController = null;
  #cardObserver = null;

  #getViewportMode() {
    return mediaQueryLarge?.matches ? 'desktop' : 'mobile';
  }

  #getStorageKey() {
    return `${FIT_VIEW_STORAGE_PREFIX}${this.#getViewportMode()}`;
  }

  #setGridView(value) {
    const { grid } = this.refs;
    if (!(grid instanceof HTMLElement)) return false;
    grid.setAttribute(FIT_GRID_ATTRIBUTE, value);
    return true;
  }

  #persistLayoutPreference(value) {
    requestIdleCallback(() => {
      try {
        sessionStorage.setItem(this.#getStorageKey(), value);
      } catch {
        // Session storage is best-effort only.
      }
    });
  }

  #resolveViewportInput(matchesDesktop) {
    const selector = matchesDesktop ? FIT_VIEWPORT_SELECTORS.desktop : FIT_VIEWPORT_SELECTORS.mobile;
    const input = this.querySelector(selector);
    if (input instanceof HTMLInputElement) return input;
    return null;
  }

  async #runViewTransition(value) {
    if (typeof startViewTransition === 'function') {
      await startViewTransition(() => this.#setGridView(value), FIT_TRANSITION_SCOPE);
      return;
    }
    this.#setGridView(value);
  }

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    if (mediaQueryLarge?.addEventListener) {
      mediaQueryLarge.addEventListener('change', this.#onBreakpoint, {
        signal: this.#abortController.signal,
        passive: true,
      });
    }

    this.setAttribute('initialized', '');
    this.#bindImageToggle();
    this.#restoreImageToggle();
    this.#observeNewCards();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#cardObserver?.disconnect();
    this.#cardObserver = null;
  }

  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;
    const nextLayout = target.value;
    if (!nextLayout || typeof nextLayout !== 'string') return;
    this.#runViewTransition(nextLayout);
    this.#persistLayoutPreference(nextLayout);
  }

  #onBreakpoint = (e) => {
    const viewportInput = this.#resolveViewportInput(e.matches);
    if (!(viewportInput instanceof HTMLInputElement)) return;
    viewportInput.checked = true;
    this.#setGridView(FIT_LAYOUT_FALLBACK);
  };

  // ── Image toggle ──────────────────────────────────────────────

  #bindImageToggle() {
    this.addEventListener('change', (e) => {
      if (!(e.target instanceof HTMLInputElement)) return;
      if (e.target.name === 'fit_image_toggle') {
        this.toggleImageView({ target: e.target });
      }
    }, { signal: this.#abortController.signal, passive: true });

    this.addEventListener(FCEvents.variantChange, this.#onVariantChange, {
      signal: this.#abortController.signal,
      capture: true,
    });
  }

  toggleImageView({ target }) {
    if (!(target instanceof HTMLInputElement)) return;
    const showSecond = target.checked;
    this.#applyImageView(showSecond);
    this.#persistImageToggle(showSecond);
    this.#syncToggleInputs(showSecond);
  }

  #applyImageView(showSecond) {
    const links = this.querySelectorAll('[data-ref="cardGalleryLink"][data-alt-image-url]');
    for (const link of links) {
      const url = showSecond ? link.dataset.altImageUrl : link.dataset.primaryImageUrl;
      if (!url) continue;
      const img = link.querySelector('img');
      if (img) {
        this.#setImageSource(img, url);
      }
    }
    this.dataset.fitBaseSlide = showSecond ? '1' : '0';
  }

  #onVariantChange = () => {
    if (this.dataset.fitBaseSlide !== '1') return;
    requestAnimationFrame(() => {
      this.#syncToggleInputs(true);
      this.#applyImageView(true);
    });
  };

  #setImageSource(img, url) {
    img.removeAttribute('srcset');
    img.srcset = '';
    img.setAttribute('src', url);
    img.src = url;

    if (img.hasAttribute('data-max-resolution')) {
      img.setAttribute('data-max-resolution', url);
    }
  }

  #persistImageToggle(showSecond) {
    try {
      localStorage.setItem(FIT_IMAGE_TOGGLE_KEY, showSecond ? '1' : '0');
    } catch {
      // localStorage is best-effort only.
    }
  }

  #restoreImageToggle() {
    try {
      const stored = localStorage.getItem(FIT_IMAGE_TOGGLE_KEY);
      if (stored !== '1') return;
      this.#syncToggleInputs(true);
      requestAnimationFrame(() => this.#applyImageView(true));
    } catch {
      // localStorage is best-effort only.
    }
  }

  #syncToggleInputs(checked) {
    const inputs = this.querySelectorAll(FIT_TOGGLE_INPUT_SELECTOR);
    for (const input of inputs) {
      if (input instanceof HTMLInputElement) input.checked = checked;
    }
  }

  #observeNewCards() {
    const { grid } = this.refs;
    if (!(grid instanceof HTMLElement)) return;

    this.#cardObserver = new MutationObserver(() => {
      if (this.dataset.fitBaseSlide !== '1') return;
      // Brief delay lets newly loaded cards finish rendering their carousels
      setTimeout(() => {
        this.#syncToggleInputs(true);
        this.#applyImageView(true);
      }, 100);
    });

    this.#cardObserver.observe(grid, { childList: true });
  }
}

if (!customElements.get(FIT_RESULTS_TAG)) {
  customElements.define(FIT_RESULTS_TAG, ResultsList);
}
